import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	useSyncExternalStore,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { stableSignedUrl } from '../utils/signedUrlCache';

import useFetch from './useFetch'; // Assuming useFetch is in the same directory

// Display-level aspect jitter (restores the old ingest-time effect, see the
// longer comment inside the hook below): deterministic per photo, hashed
// from _id with djb2 — never Math.random(), or the grid would wiggle on
// every refetch. Output in [-ASPECT_JITTER, +ASPECT_JITTER].
const ASPECT_JITTER = 0.05;

const aspectNudge = (id: string): number => {
	let h = 5381;
	for (let i = 0; i < id.length; i++) {
		h = (h * 33 + id.charCodeAt(i)) >>> 0;
	}
	return ((h / 4294967295) - 0.5) * 2 * ASPECT_JITTER;
};

// MatchMedia listener for the jitter gate (useSyncExternalStore keeps this
// resilient to concurrent updates).
const narrowQuery = '(max-width: 768px)'; // codebase's standard mobile breakpoint
const subscribeNarrow = (cb: () => void) => {
	const mq = window.matchMedia(narrowQuery);
	mq.addEventListener('change', cb);
	return () => mq.removeEventListener('change', cb);
};
const isNarrowSnapshot = () => window.matchMedia(narrowQuery).matches;

const useIsNarrowViewport = (): boolean =>
	useSyncExternalStore(subscribeNarrow, isNarrowSnapshot, () => false);

// TODO: Move these interfaces to a shared types file (e.g., src/types/gallery.ts)
export interface MediaItem {
	_id: string;
	path: string;
	type: 'image' | 'video';
	thumbnail_path: string;
	compressed_path?: string;
	original_file_size?: number;
	file_size: number;
	width: number;
	height: number;
	rotation?: number;
	thumbnail_width: number;
	thumbnail_height: number;
	upload_date: string;
	album: string;
	isSecret?: boolean;
	albumName?: string;
	unitat?: string;
}

interface MediaList {
	mediaItems: MediaItem[];
	pageCount: number;
	totalCount?: number; // Add totalCount
	secretCount?: number; // Add secretCount
}

interface GalleryImage {
	_id?: string; // Keep optional _id consistent if added elsewhere
	src: string;
	width: number;
	height: number;
	caption?: string;
}

interface UseMediaGalleryProps {
	initialAlbumId?: string;
	initialUnitat?: string;
	initialPageSize?: number;
	initialPage?: number;
	initialSortField?: string;
	initialSortDirection?: string;
	/** Controlled pagination/sort (preferred): when provided the hook owns
	 *  NO mirror state — it fetches with these and reports changes through
	 *  the matching callback. Falls back to internal state otherwise. */
	page?: number;
	pageSize?: number;
	sortField?: string;
	sortDirection?: string;
	onPageChange?: (page: number) => void;
	minYear?: string; // School year filter (e.g., "2023-2024")
	maxYear?: string; // School year filter (e.g., "2023-2024")
	onSortFieldChange?: (field: string) => void;
	onSortDirectionChange?: (direction: string) => void;
	onlySecret?: boolean; // New prop
	includeSecret?: boolean; // New prop
	// itemId -> total rotation (persisted already combined with pending delta).
	// Used so the justified-layout slot reflects a PENDING rotation live
	// instead of only applying after confirm.
	/** Pending rotation DELTAS (±90) by item id; combined with the persisted
	 *  rotation inside this hook so thumbnail slots reflow live. */
	pendingDeltas?: Map<string, number>;
}

interface UseMediaGalleryReturn {
	pictureList: MediaList | null | undefined;
	isLoading: boolean;
	error: Error | null;
	thumbnailImages: GalleryImage[];
	page: number;
	pageSize: number;
	sortField: string;
	sortDirection: string;
	handlePageChange: (newPage: number) => void;
	handlePageSizeChange: (newSize: number) => void;
	handleSortFieldChange: (newField: string) => void;
	handleSortDirectionChange: (newDirection: string) => void;
	albumId?: string; // Return resolved albumId
	paramUnitat?: string; // Return resolved unitat
	seed: number; // Add seed to the return type
}

const useMediaGallery = ({
	initialAlbumId,
	initialUnitat,
	initialPageSize = 20,
	initialPage = 1,
	//
	page: controlledPage,
	pageSize: controlledPageSize,
	sortField: controlledSortField,
	sortDirection: controlledSortDirection,
	onPageChange,
	initialSortField = 'upload_date',
	initialSortDirection = 'desc',
	minYear,
	maxYear,
	onSortFieldChange,
	onSortDirectionChange,
	onlySecret,
	includeSecret,
	pendingDeltas,
}: UseMediaGalleryProps): UseMediaGalleryReturn => {
	const params = useParams();
	// Resolve albumId and unitat from props or URL params
	// Exclude special route values ('tots', 'new') that are not real album IDs
	const rawAlbumId = initialAlbumId || params.albumId;
	const albumId =
		rawAlbumId === 'tots' || rawAlbumId === 'new'
			? undefined
			: rawAlbumId;
	const paramUnitat = initialUnitat || params.unitat;

	// Single ownership: controlled props when provided, internal state
	// otherwise (no mirror effects — the old setX(initialX) loops).
	const [internalSortField, setInternalSortField] = useState(initialSortField);
	const [internalSortDirection, setInternalSortDirection] =
		useState(initialSortDirection);
	const [internalPage, setInternalPage] = useState(initialPage);
	const [internalPageSize, setInternalPageSize] = useState(initialPageSize);

	const sortField = controlledSortField ?? internalSortField;
	const sortDirection = controlledSortDirection ?? internalSortDirection;
	const page = controlledPage ?? internalPage;
	const pageSize = controlledPageSize ?? internalPageSize;

	const [seed, setSeed] = useState(Math.random()); // Seed for random sort

	// Reports pagination changes to the controller when controlled.
	const setLocalPage = useCallback(
		(p: number) => {
			if (controlledPage != null) onPageChange?.(p);
			else setInternalPage(p);
		},
		[controlledPage, onPageChange]
	);

	// Build URL with optional year filters
	const buildUrl = () => {
		let url = `/api/galeria/gallery-items${
			albumId ? `/a/${albumId}` : paramUnitat ? `/u/${paramUnitat}` : ''
		}?sortField=${sortField}&sortDirection=${sortDirection}&page=${page}&size=${pageSize}&seed=${seed}`;

		if (minYear) {
			url += `&minYear=${minYear}`;
		}
		if (maxYear) {
			url += `&maxYear=${maxYear}`;
		}
		if (onlySecret) {
			url += '&onlySecret=true';
		}
		if (includeSecret) {
			url += '&includeSecret=true';
		}
		return url;
	};

	// Fetching logic using useFetch
	const {
		data: pictureList,
		isLoading,
		error,
	} = useFetch<MediaList>({
		queryKey: [
			'pictures',
			albumId
				? `album${albumId}`
				: paramUnitat
				? `unitat${paramUnitat}`
				: 'totes',
			page,
			pageSize,
			sortField,
			sortDirection,
			seed,
			minYear || 'all',
			maxYear || 'all',
			onlySecret ? 'secret' : 'public', // Add to query key
			includeSecret ? 'includeSecret' : 'excludeSecret', // Add to query key
		],
		url: buildUrl(),
	});



	// Handlers moved from Pictures.tsx
	const handlePageChange = useCallback(
		(newPage: number) => {
			setLocalPage(newPage);
		},
		[setLocalPage]
	);

	const handleSortFieldChange = useCallback(
		(newField: string) => {
			if (newField === 'random') {
				const newSeed = Math.random();
				setSeed(newSeed);
				// Persist seed for consistent random order across sessions
				localStorage.setItem('gallery_seed', newSeed.toString());
			}
			if (controlledSortField != null) {
				// The controller owns the sort field; report the change.
				onSortFieldChange?.(newField);
			} else {
				setInternalSortField(newField);
				onSortFieldChange?.(newField);
			}
			setLocalPage(1); // Reset to first page on sort change
		},
		[controlledSortField, onSortFieldChange, setLocalPage, setSeed]
	);

	const handleSortDirectionChange = useCallback(
		(newDirection: string) => {
			if (controlledSortDirection != null) {
				// The controller owns the sort direction; report the change.
				onSortDirectionChange?.(newDirection);
			} else {
				setInternalSortDirection(newDirection);
				onSortDirectionChange?.(newDirection);
			}
			setLocalPage(1); // Reset to first page on sort change
		},
		[controlledSortDirection, onSortDirectionChange, setLocalPage]
	);

	const handlePageSizeChange = useCallback(
		(newSize: number) => {
			// In controlled mode the page-size change is already applied by
			// the controller's own handler; only the page reset is ours.
			if (controlledPageSize == null) setInternalPageSize(newSize);
			setLocalPage(1); // Reset to first page on size change
		},
		[controlledPageSize, setLocalPage]
	);


	// Display-level aspect jitter (restores the old ingest-time effect).
	// The original pipeline randomly cropped each stored thumbnail a few
	// percent, so even an album of identical-aspect photos produced an
	// organic justified-row mosaic instead of uniform bricks. Thumbnails now
	// ship uncropped, but the layout here only consumes numbers, and
	// ThumbnailImage paints with object-fit: cover — so we fake the variety
	// at layout time: each item's layout aspect is nudged by up to
	// ASPECT_JITTER, hashed from the item _id (NEVER Math.random(): the same
	// photo must keep the same shape across renders, pages and reloads, or
	// the grid visibly wiggles on every refetch). The real <img>, covering
	// the slightly-different slot, gets trimmed by a couple of edge percent
	// — the old random-crop look with zero stored crops, reversible by
	// setting ASPECT_JITTER to 0. Lightbox + GCS objects + signed-URL
	// caching are untouched (srcs don't change).

	// Jitter gate: off on mobile (<=768px, codebase breakpoint) — only ~2
	// columns fit, so varied slots read as uneven, not organic.
	// ?jitter=1 forces the effect ON (mobile comparison), ?jitter=0 forces
	// it OFF (desktop) — overriding the default by viewport, per URL.
	const isNarrow = useIsNarrowViewport();
	const [searchParams] = useSearchParams();
	const jitterOverride = searchParams.get('jitter');
	const jitterEnabled =
		jitterOverride === '1' ? true : jitterOverride === '0' ? false : !isNarrow;

	// Derived (pure): thumbnails formatted from the picture list. LIVE this
	// way the list never flashes empty while reformatting on page changes.
	const thumbnailImages = useMemo(() => {
		if (pictureList) {
			const formattedThumbnails = pictureList.mediaItems.map(
				(mediaItem) => {
					// Lossless display rotation: for 90/270 the visible thumbnail
					// aspect is swapped (the pixels themselves are not rotated).
					// Effective rotation = persisted field, or a live pending
					// rotation (base + pending delta already combined by the
					// caller) so the slot reflows DURING the preview.
					const delta = pendingDeltas?.get(mediaItem._id) ?? 0;
					const rot =
						delta !== 0
							? ((((mediaItem.rotation ?? 0) + delta) %
									360) +
								360) %
								360
							: (mediaItem.rotation ?? 0);
					const isRotated90 = rot === 90 || rot === 270;
					const tw = isRotated90
						? mediaItem.thumbnail_height
						: mediaItem.thumbnail_width;
					const th = isRotated90
						? mediaItem.thumbnail_width
						: mediaItem.thumbnail_height;
					// Nudge derives from the LAYOUT aspect only (applied to the final
					// width / fixed-180 height), after the rotation-aware tw/th swap,
					// so rotated items get the same organic variation. The native
					// aspect stays available to the exact-fill rotation math inside
					// CustomThumbnail from the slot the layout engine hands it.
					// GATED OFF on mobile (<=768px): only ~2 columns fit there, so
					// varying the tiles just looks uneven, not organic.
					const nudge = jitterEnabled ? aspectNudge(mediaItem._id) : 0;
					return {
						// _id: mediaItem._id, // Include _id if needed by GalleryImage
						// Reuse the windowed signed URL so the browser serves thumbnails
						// from its HTTP cache on repeat views (see signedUrlCache.ts).
						src: stableSignedUrl(mediaItem.thumbnail_path),
						// Calculate width based on fixed height 180, maintaining aspect ratio
						// Ensure thumbnail_height is not zero to avoid division by zero
						// × (1 + nudge) = the display-level aspect jitter (see block above).
						width:
							(th ? (tw * 180) / th : 180 * (16 / 9)) *
							(1 + nudge), // Default to 16:9 aspect ratio if height is 0
						height: 180,
					};
				}
			);
			return formattedThumbnails;
		}
		return [];
	}, [pictureList, pendingDeltas, jitterEnabled]);

	// Effect to load initial seed for random sort from localStorage
	useEffect(() => {
		const seedFromLocalStorage = localStorage.getItem('gallery_seed');
		if (seedFromLocalStorage) {
			setSeed(parseFloat(seedFromLocalStorage));
		}
	}, []); // Run only once on mount

	// Reset page to 1 when albumId or unitat changes. MUST fire only on a
	// real album/unitat change: in controlled mode setLocalPage's identity
	// churns on every page change (controlledPage is in its deps — the
	// parent owns page state), and listing the setter itself in the deps
	// re-ran this effect after EVERY navigation, snapping the user straight
	// back to page 1 (regression from the controlled-mode rework). Keep a
	// stable ref to the setter instead.
	const setLocalPageRef = useRef(setLocalPage);
	useEffect(() => {
		setLocalPageRef.current = setLocalPage;
	});
	useEffect(() => {
		setLocalPageRef.current(1);
	}, [albumId, paramUnitat]); // resolved values only

	return {
		pictureList,
		isLoading,
		error,
		thumbnailImages,
		page,
		pageSize,
		sortField,
		sortDirection,
		handlePageChange,
		handlePageSizeChange,
		handleSortFieldChange,
		handleSortDirectionChange,
		albumId, // Return resolved values
		paramUnitat, // Return resolved values
		seed, // Return the current seed value
	};
};

export default useMediaGallery;
